/**
 * The route tree (code-based TanStack Router). Layout routes decide chrome
 * and access:
 *
 *   /_public/*      signed-out screens, fullscreen, no session required
 *   /_fullscreen/*  standalone screens that still need a session
 *   /_shell/*       everything else: sidebar shell, session required
 *
 * Every screen from apps/web's customer router is registered (Market Spy is
 * retired). Each route declares `staticData.title` (page title + mobile header).
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type ParsedLocation,
} from '@tanstack/react-router';
import { AppShell } from '@/app/AppShell';
import { FullscreenLayout, RootLayout } from '@/app/RootLayout';
import { RouteErrorPage } from '@/app/RouteErrorPage';
import { SCREENS } from '@/app/screens';
import { researchSearch } from '@/features/competitor-insights/search';
import { performanceSearch } from '@/features/performance/search';
import { overviewSearch } from '@/features/overview/search';
import { DEFAULT_AUTHENTICATED_PATH } from '@/auth/session';
import { resolveSession } from '@/auth/sessionService';
import { sessionQueries } from '@/auth/useSession';
import { creativeLabSearch, replacementSearch, workspaceSearch } from '@/features/creative-lab/search';
import { exploreSearch, insightsSearch } from '@/features/attribute-insights/search';
import { hasLegacyKeys, legacyComparisonHref, legacyExploreHref, legacyInsightsHref } from '@/features/attribute-insights/legacy';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';
import { metaSearch } from '@/features/connections/search';
import { apiAccessSearch, consentSearch } from '@/features/api-access/search';
import { aiResizingSearch } from '@/features/ai-resizing/search';
import { forecastSearch } from '@/features/revenue-forecasts/search';
import { defineSearch, param, parseSearch, stringifySearch } from './search';

export type RouterContext = { queryClient: QueryClient };

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
  interface StaticDataRouteOption {
    /** Page title; also shown in the mobile header. */
    title?: string;
  }
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

/** Signed-out visits go to /login?redirect=<where they were going> (parity §0). */
const requireSession = async ({ location }: { location: ParsedLocation }) => {
  if (await resolveSession()) return;
  const target = location.href;
  throw redirect({
    to: '/login',
    search: target === DEFAULT_AUTHENTICATED_PATH ? {} : { redirect: target },
    replace: true,
  });
};

/** Keep the query string and fragment when a path is renamed. */
const moved = (to: string) => ({ location }: { location: ParsedLocation }) => {
  throw redirect({ href: `${to}${location.searchStr}${location.hash ? `#${location.hash}` : ''}`, replace: true });
};

// --- Layouts ----------------------------------------------------------------

const publicLayout = createRoute({ getParentRoute: () => rootRoute, id: '_public', component: FullscreenLayout });
const fullscreenLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: '_fullscreen',
  beforeLoad: requireSession,
  component: FullscreenLayout,
});
const shellLayout = createRoute({ getParentRoute: () => rootRoute, id: '_shell', beforeLoad: requireSession, component: AppShell });

// --- Renamed paths ----------------------------------------------------------

/** `/` and the old `/asset-performance` both land on Performance, query intact. */
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', beforeLoad: moved(SCREENS.performance.path) });
const assetPerformanceRoute = createRoute({ getParentRoute: () => rootRoute, path: '/asset-performance', beforeLoad: moved(SCREENS.performance.path) });
const assetAttributesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/asset-attributes', beforeLoad: moved(SCREENS.attributes.path) });
/** apps/web's Attribute analysis paths (features/attribute-insights/legacy.ts): every old link keeps working. */
const legacyRedirect = (target: (searchStr: string, hash: string) => string) => ({ location }: { location: ParsedLocation }) => {
  throw redirect({ href: target(location.searchStr, location.hash), replace: true });
};
const attributeAnalysisRoute = createRoute({ getParentRoute: () => rootRoute, path: '/attribute-analysis', beforeLoad: legacyRedirect(legacyInsightsHref) });
const attributeAnalysisExploreRoute = createRoute({ getParentRoute: () => rootRoute, path: '/attribute-analysis/explore', beforeLoad: legacyRedirect(legacyExploreHref) });
const attributeComparisonRoute = createRoute({ getParentRoute: () => rootRoute, path: '/attribute-analysis/comparison', beforeLoad: legacyRedirect(legacyComparisonHref) });
/** apps/web's Creative Lab paths: the home and a project (`chat/:chatId`), query intact. */
const creativeLabV2Route = createRoute({ getParentRoute: () => rootRoute, path: '/creative-lab-v2', beforeLoad: moved(SCREENS.creativeLab.path) });
const creativeLabV2ChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/creative-lab-v2/chat/$chatId',
  beforeLoad: ({ location, params }) => { moved(`/creative-lab/projects/${encodeURIComponent(params.chatId)}`)({ location }); },
});

// --- Public -----------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/login',
  staticData: { title: SCREENS.login.title },
  validateSearch: defineSearch((raw) => ({
    redirect: param.string(raw, 'redirect'),
    auth_error: param.string(raw, 'auth_error'),
    mfa: param.string(raw, 'mfa'),
  })),
  component: lazyRouteComponent(() => import('@/features/auth/LoginPage'), 'LoginPage'),
});
const supportSessionRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/support-session',
  staticData: { title: SCREENS.supportSession.title },
  component: lazyRouteComponent(() => import('@/features/auth/SupportSessionPage'), 'SupportSessionPage'),
});

// --- Session required, no shell -----------------------------------------------

const metaAdsLoginRoute = createRoute({
  getParentRoute: () => fullscreenLayout,
  path: SCREENS.metaAdsLogin.path,
  staticData: { title: SCREENS.metaAdsLogin.title },
  // ?connection_id= (reconnect) or Facebook's return (code, state, error, error_description).
  validateSearch: metaSearch,
  component: lazyRouteComponent(() => import('@/features/connections/MetaAdsLoginPage'), 'MetaAdsLoginPage'),
});
const oauthConsentRoute = createRoute({
  getParentRoute: () => fullscreenLayout,
  path: SCREENS.oauthConsent.path,
  staticData: { title: SCREENS.oauthConsent.title },
  validateSearch: consentSearch,
  component: lazyRouteComponent(() => import('@/features/api-access/OAuthConsentPage'), 'OAuthConsentPage'),
});

// --- Shell ------------------------------------------------------------------

const performanceRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.performance.path,
  staticData: { title: SCREENS.performance.title },
  validateSearch: performanceSearch,
  component: lazyRouteComponent(() => import('@/features/performance/PerformancePage'), 'PerformancePage'),
});

const attributeInsightsRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.attributeInsights.path,
  staticData: { title: SCREENS.attributeInsights.title },
  validateSearch: insightsSearch,
  component: lazyRouteComponent(() => import('@/features/attribute-insights/InsightsPage'), 'InsightsPage'),
});
const attributeExploreRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.attributeExplore.path,
  staticData: { title: SCREENS.attributeExplore.title },
  // apps/web's attribute_filter_key/value are read as attribute/value, then dropped.
  beforeLoad: ({ location }) => {
    if (hasLegacyKeys(location.searchStr)) throw redirect({ href: legacyExploreHref(location.searchStr, location.hash), replace: true });
  },
  validateSearch: exploreSearch,
  component: lazyRouteComponent(() => import('@/features/attribute-insights/InsightsPage'), 'ExplorePage'),
});

const marketSpyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/market-spy', beforeLoad: moved(SCREENS.competitorInsights.path) });
const competitorInsightsRoute = createRoute({
  getParentRoute: () => shellLayout, path: SCREENS.competitorInsights.path,
  staticData: { title: SCREENS.competitorInsights.title }, validateSearch: researchSearch,
  component: lazyRouteComponent(() => import('@/features/competitor-insights/CompetitorInsightsPage'), 'CompetitorInsightsPage'),
});

const creativeLabRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.creativeLab.path,
  staticData: { title: SCREENS.creativeLab.title },
  validateSearch: creativeLabSearch,
  component: lazyRouteComponent(() => import('@/features/creative-lab/home/CreativeLabPage'), 'CreativeLabPage'),
});
const creativeLabReplacementRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.creativeLabReplacement.path,
  staticData: { title: SCREENS.creativeLabReplacement.title },
  validateSearch: replacementSearch,
  component: lazyRouteComponent(() => import('@/features/creative-lab/replacement/NewReplacementPage'), 'NewReplacementPage'),
});
const creativeLabProjectRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.creativeLabProject.path,
  staticData: { title: SCREENS.creativeLabProject.title },
  validateSearch: workspaceSearch,
  component: lazyRouteComponent(() => import('@/features/creative-lab/workspace/WorkspacePage'), 'WorkspacePage'),
});

const forecastAdminRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.forecastAdmin.path,
  staticData: { title: SCREENS.forecastAdmin.title },
  // Revalidated on every entry so a revoked assignment does not keep access.
  beforeLoad: async ({ context }) => {
    const allowed = await context.queryClient.query({ ...sessionQueries.predictionOperator(), staleTime: 0 });
    if (!allowed) throw redirect({ to: SCREENS.revenueForecasts.path, replace: true });
  },
  component: lazyRouteComponent(() => import('@/features/revenue-forecasts/ForecastAdminPage'), 'ForecastAdminPage'),
});

// --- Feature routes (each feature owns its page + search schema) -----------

const overviewRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.overview.path,
  staticData: { title: SCREENS.overview.title },
  validateSearch: overviewSearch,
  component: lazyRouteComponent(() => import('@/features/overview/OverviewPage'), 'OverviewPage'),
});
const attributesRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.attributes.path,
  staticData: { title: SCREENS.attributes.title },
  component: lazyRouteComponent(() => import('@/features/attributes/AttributesPage'), 'AttributesPage'),
});
const alertsRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.alerts.path,
  staticData: { title: SCREENS.alerts.title },
  // `alert` is the Overview's deep link; `new=target` opens an account target
  // (Performance links here); `slack`/`workspace`/`code` are Slack's OAuth return.
  validateSearch: defineSearch((raw) => ({
    alert: param.string(raw, 'alert'),
    new: param.enumValue(raw, 'new', ['target'] as const),
    slack: param.string(raw, 'slack'),
    workspace: param.string(raw, 'workspace'),
    code: param.string(raw, 'code'),
  })),
  component: lazyRouteComponent(() => import('@/features/alerts/AlertsPage'), 'AlertsPage'),
});
const aiResizingRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.aiResizing.path,
  staticData: { title: SCREENS.aiResizing.title },
  validateSearch: aiResizingSearch,
  component: lazyRouteComponent(() => import('@/features/ai-resizing/AiResizingPage'), 'AiResizingPage'),
});
const revenueForecastsRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.revenueForecasts.path,
  staticData: { title: SCREENS.revenueForecasts.title },
  validateSearch: forecastSearch,
  component: lazyRouteComponent(() => import('@/features/revenue-forecasts/RevenueForecastsPage'), 'RevenueForecastsPage'),
});
const connectionsRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.connections.path,
  staticData: { title: SCREENS.connections.title },
  component: lazyRouteComponent(() => import('@/features/connections/ConnectionsPage'), 'ConnectionsPage'),
});
const metricWeightsRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.metricWeights.path,
  staticData: { title: SCREENS.metricWeights.title },
  component: lazyRouteComponent(() => import('@/features/metric-weights/MetricWeightsPage'), 'MetricWeightsPage'),
});
const apiAccessRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.apiAccess.path,
  staticData: { title: SCREENS.apiAccess.title },
  validateSearch: apiAccessSearch,
  component: lazyRouteComponent(() => import('@/features/api-access/ApiAccessPage'), 'ApiAccessPage'),
});
const usersRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.users.path,
  staticData: { title: SCREENS.users.title },
  component: lazyRouteComponent(() => import('@/features/users/UsersPage'), 'UsersPage'),
});
const billingRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.billing.path,
  staticData: { title: SCREENS.billing.title },
  // Stripe returns to ?checkout=success|cancelled (features/billing/search.ts reads it).
  validateSearch: defineSearch((raw) => ({ checkout: param.enumValue(raw, 'checkout', ['success', 'cancelled', 'cancel'] as const) })),
  component: lazyRouteComponent(() => import('@/features/billing/BillingPage'), 'BillingPage'),
});
const noConnectionsRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.noConnections.path,
  staticData: { title: SCREENS.noConnections.title },
  component: lazyRouteComponent(() => import('@/features/connections/NoConnectionsPage'), 'NoConnectionsPage'),
});
const tiktokAdsConnectedRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.tiktokAdsConnected.path,
  staticData: { title: SCREENS.tiktokAdsConnected.title },
  validateSearch: defineSearch((raw) => ({ error: param.string(raw, 'error'), connection_id: param.string(raw, 'connection_id') })),
  component: lazyRouteComponent(() => import('@/features/connections/TikTokAdsConnectedPage'), 'TikTokAdsConnectedPage'),
});
const googleAdsConnectedRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.googleAdsConnected.path,
  staticData: { title: SCREENS.googleAdsConnected.title },
  component: lazyRouteComponent(() => import('@/features/connections/GoogleAdsConnectedPage'), 'GoogleAdsConnectedPage'),
});
const youtubeConnectedRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: SCREENS.youtubeConnected.path,
  staticData: { title: SCREENS.youtubeConnected.title },
  component: lazyRouteComponent(() => import('@/features/connections/YouTubeConnectedPage'), 'YouTubeConnectedPage'),
});
const signupRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: SCREENS.signup.path,
  staticData: { title: SCREENS.signup.title },
  component: lazyRouteComponent(() => import('@/features/auth/SignupPage'), 'SignupPage'),
});
const verificationRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: SCREENS.verification.path,
  staticData: { title: SCREENS.verification.title },
  // From signup: email_address + user_id; from the emailed link: + signup_secret.
  validateSearch: defineSearch((raw) => ({
    email_address: param.string(raw, 'email_address'),
    user_id: param.string(raw, 'user_id'),
    signup_secret: param.string(raw, 'signup_secret'),
  })),
  component: lazyRouteComponent(() => import('@/features/auth/VerificationPage'), 'VerificationPage'),
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: SCREENS.forgotPassword.path,
  staticData: { title: SCREENS.forgotPassword.title },
  // The emailed reset link: reset_password_secret + user_id + email_address.
  validateSearch: defineSearch((raw) => ({
    email_address: param.string(raw, 'email_address'),
    user_id: param.string(raw, 'user_id'),
    reset_password_secret: param.string(raw, 'reset_password_secret'),
  })),
  component: lazyRouteComponent(() => import('@/features/auth/ForgotPasswordPage'), 'ForgotPasswordPage'),
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: SCREENS.resetPassword.path,
  staticData: { title: SCREENS.resetPassword.title },
  // The emailed reset link: reset_password_secret + user_id + email_address.
  validateSearch: defineSearch((raw) => ({
    email_address: param.string(raw, 'email_address'),
    user_id: param.string(raw, 'user_id'),
    reset_password_secret: param.string(raw, 'reset_password_secret'),
  })),
  component: lazyRouteComponent(() => import('@/features/auth/ResetPasswordPage'), 'ResetPasswordPage'),
});

const notFoundRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: '$',
  staticData: { title: SCREENS.notFound.title },
  component: NotFoundPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  marketSpyRoute,
  assetPerformanceRoute,
  assetAttributesRoute,
  attributeAnalysisRoute,
  attributeAnalysisExploreRoute,
  attributeComparisonRoute,
  creativeLabV2Route,
  creativeLabV2ChatRoute,
  publicLayout.addChildren([
    loginRoute,
    supportSessionRoute,
    signupRoute,
    verificationRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
  ]),
  fullscreenLayout.addChildren([metaAdsLoginRoute, oauthConsentRoute]),
  shellLayout.addChildren([
    performanceRoute,
    attributeInsightsRoute,
    forecastAdminRoute,
    overviewRoute,
    attributeExploreRoute,
    creativeLabRoute,
    competitorInsightsRoute,
    creativeLabReplacementRoute,
    creativeLabProjectRoute,
    attributesRoute,
    alertsRoute,
    aiResizingRoute,
    revenueForecastsRoute,
    connectionsRoute,
    metricWeightsRoute,
    apiAccessRoute,
    usersRoute,
    billingRoute,
    noConnectionsRoute,
    googleAdsConnectedRoute,
    tiktokAdsConnectedRoute,
    youtubeConnectedRoute,
    notFoundRoute,
  ]),
]);

export const createAppRouter = (queryClient: QueryClient) =>
  createRouter({
    routeTree,
    context: { queryClient },
    parseSearch,
    stringifySearch,
    defaultPreload: 'intent',
    // Preloaded data is owned by TanStack Query, not the router cache.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultErrorComponent: RouteErrorPage,
  });

export type AppRouter = ReturnType<typeof createAppRouter>;
